import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  eggProductionApi, poultryBatchesApi, poultryFeedRecordsApi, poultryKpisApi, poultryMortalityApi,
} from '../../services/resources';
import { formatDate, formatNumber, shortId, today } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import type { EggProduction, PoultryBatch, PoultryFeedRecord, PoultryKPI, PoultryMortality } from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: Poultry_Batches plus its four daily-entry children
// (Mortality/Eggs/Feed/KPIs, REQ-017/018) are steps of one batch lifecycle
// (WF-06), so they're tabs on one "Poultry" page.

const batchesConfig: ModuleConfig<PoultryBatch> = {
  title: 'Batches', entityLabel: 'batch', module: 'Poultry', queryKey: 'poultry-batches', api: poultryBatchesApi, idKey: 'id',
  referenceKeys: ['farms', 'sheds', 'breeds'], defaultValues: { bird_type: 'Layer', status: 'Active', placement_date: today(), additions: 0 },
  columns: [
    { key: 'batch_no', header: 'Batch No.', primary: true, render: (r) => r.batch_no },
    { key: 'type', header: 'Type', render: (r) => r.bird_type },
    { key: 'placement', header: 'Placement', render: (r) => formatDate(r.placement_date) },
    { key: 'birds', header: 'Current Birds', align: 'right', render: (r) => r.current_birds },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Batch', required: true, optionsKey: 'farms' },
    { name: 'batch_no', label: 'Batch Number', type: 'text', section: 'Batch', required: true },
    { name: 'bird_type', label: 'Bird Type', type: 'select', section: 'Batch', required: true, staticOptions: [{ value: 'Layer', label: 'Layer' }, { value: 'Broiler', label: 'Broiler' }] },
    { name: 'breed_id', label: 'Breed', type: 'select', section: 'Batch', optional: true, optionsKey: 'breeds' },
    { name: 'shed_id', label: 'Shed', type: 'select', section: 'Batch', required: true, optionsKey: 'sheds' },
    { name: 'placement_date', label: 'Placement Date', type: 'date', section: 'Batch', required: true },
    { name: 'opening_birds', label: 'Opening Birds', type: 'number', section: 'Birds', required: true, min: 0 },
    { name: 'additions', label: 'Additions', type: 'number', section: 'Birds', optional: true, min: 0 },
    { name: 'status', label: 'Status', type: 'select', section: 'Birds', required: true, staticOptions: [{ value: 'Active', label: 'Active' }, { value: 'Closed', label: 'Closed' }] },
  ],
};

const mortalityConfig: ModuleConfig<PoultryMortality> = {
  title: 'Mortality', entityLabel: 'mortality record', module: 'Poultry', queryKey: 'poultry-mortality', api: poultryMortalityApi, idKey: 'id',
  referenceKeys: ['poultryBatches'], defaultValues: { mortality_date: today() },
  columns: [
    { key: 'batch', header: 'Batch', primary: true, render: (r) => shortId(r.batch_id) },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.mortality_date) },
    { key: 'count', header: 'Birds Lost', align: 'right', render: (r) => r.bird_count },
    { key: 'cause', header: 'Cause', render: (r) => r.cause || '—' },
  ],
  fields: [
    { name: 'batch_id', label: 'Batch', type: 'select', section: 'Mortality', required: true, optionsKey: 'poultryBatches' },
    { name: 'mortality_date', label: 'Date', type: 'date', section: 'Mortality', required: true },
    { name: 'bird_count', label: 'Birds Lost', type: 'number', section: 'Mortality', required: true, min: 0 },
    { name: 'cause', label: 'Cause', type: 'text', section: 'Mortality', optional: true },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Mortality', optional: true, fullWidth: true },
  ],
};

const eggsConfig: ModuleConfig<EggProduction> = {
  title: 'Egg Production', entityLabel: 'egg record', module: 'Poultry', queryKey: 'egg-production', api: eggProductionApi, idKey: 'id',
  referenceKeys: ['poultryBatches', 'sheds'], addLabel: 'Record Eggs',
  defaultValues: { production_date: today(), grade_a: 0, grade_b: 0, broken_eggs: 0, total_eggs: 0 },
  columns: [
    { key: 'batch', header: 'Batch', primary: true, render: (r) => shortId(r.batch_id) },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.production_date) },
    { key: 'grade_a', header: 'Grade A', align: 'right', render: (r) => r.grade_a },
    { key: 'grade_b', header: 'Grade B', align: 'right', render: (r) => r.grade_b },
    { key: 'broken', header: 'Broken', align: 'right', render: (r) => r.broken_eggs, hideOnTablet: true },
    { key: 'total', header: 'Total', align: 'right', render: (r) => r.total_eggs },
  ],
  fields: [
    { name: 'batch_id', label: 'Batch', type: 'select', section: 'Eggs', required: true, optionsKey: 'poultryBatches' },
    { name: 'shed_id', label: 'Shed', type: 'select', section: 'Eggs', required: true, optionsKey: 'sheds' },
    { name: 'production_date', label: 'Date', type: 'date', section: 'Eggs', required: true },
    { name: 'grade_a', label: 'Grade A', type: 'number', section: 'Counts', required: true, min: 0 },
    { name: 'grade_b', label: 'Grade B', type: 'number', section: 'Counts', required: true, min: 0 },
    { name: 'broken_eggs', label: 'Broken', type: 'number', section: 'Counts', required: true, min: 0 },
    { name: 'total_eggs', label: 'Total', type: 'number', section: 'Counts', required: true, min: 0 },
  ],
};

const feedConfig: ModuleConfig<PoultryFeedRecord> = {
  title: 'Batch Feed', entityLabel: 'feed record', module: 'Poultry', queryKey: 'poultry-feed-records', api: poultryFeedRecordsApi, idKey: 'id',
  referenceKeys: ['poultryBatches', 'feedItems'], defaultValues: { feed_date: today() },
  columns: [
    { key: 'batch', header: 'Batch', primary: true, render: (r) => shortId(r.batch_id) },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.feed_date) },
    { key: 'quantity', header: 'Quantity (kg)', align: 'right', render: (r) => formatNumber(r.quantity_kg, 2) },
  ],
  fields: [
    { name: 'batch_id', label: 'Batch', type: 'select', section: 'Feed', required: true, optionsKey: 'poultryBatches' },
    { name: 'feed_item_id', label: 'Feed Item', type: 'select', section: 'Feed', required: true, optionsKey: 'feedItems' },
    { name: 'feed_date', label: 'Date', type: 'date', section: 'Feed', required: true },
    { name: 'quantity_kg', label: 'Quantity (kg)', type: 'decimal', section: 'Feed', required: true, min: 0 },
  ],
};

const kpiConfig: ModuleConfig<PoultryKPI> = {
  title: 'KPIs', entityLabel: 'KPI snapshot', module: 'Poultry', queryKey: 'poultry-kpis',
  api: { list: poultryKpisApi.list, create: async () => { throw new Error('read-only — use Recompute'); }, update: async () => { throw new Error('read-only'); } },
  idKey: 'id', readOnly: true,
  columns: [
    { key: 'batch', header: 'Batch', primary: true, render: (r) => shortId(r.batch_id) },
    { key: 'as_of', header: 'As Of', render: (r) => formatDate(r.as_of_date) },
    { key: 'hdep', header: 'HDEP %', align: 'right', render: (r) => formatNumber(r.hen_day_egg_percent, 1) },
    { key: 'fcr', header: 'FCR', align: 'right', render: (r) => formatNumber(r.feed_conversion_ratio, 2) },
    { key: 'mortality', header: 'Mortality %', align: 'right', render: (r) => formatNumber(r.mortality_rate_percent, 2) },
    { key: 'livability', header: 'Livability %', align: 'right', render: (r) => formatNumber(r.livability_percent, 1) },
  ],
  fields: [],
};

export default function PoultryPage() {
  return (
    <TabbedPage
      title="Poultry"
      subtitle="Layer/broiler batches, mortality, eggs, feed and FCR/HDEP KPIs (WF-06)."
      tabs={[
        { id: 'batches', label: 'Batches', render: () => <ResourceModule config={batchesConfig} /> },
        { id: 'mortality', label: 'Mortality', render: () => <ResourceModule config={mortalityConfig} /> },
        { id: 'eggs', label: 'Eggs', render: () => <ResourceModule config={eggsConfig} /> },
        { id: 'feed', label: 'Feed', render: () => <ResourceModule config={feedConfig} /> },
        { id: 'kpis', label: 'KPIs', render: () => <ResourceModule config={kpiConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Poultry', batchesConfig, mortalityConfig, eggsConfig, feedConfig);
